import {
    Box,
    Grid,
    Paper,
    Typography,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    FormControlLabel,
    RadioGroup,
    Radio,
    IconButton,
    Chip,
    Card,
    CardContent,
    CardActions
} from "@mui/material";
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    DragIndicator as DragIcon
} from "@mui/icons-material";
import { useCallback } from "react";

interface ISpecFormulario {
    label: string;
    mask: string;
    name: string;
    required: boolean;
    type: string;
    options?: string[];
}

interface IFormBuilderProps {
    spec?: ISpecFormulario[];
    possuiEdicao: boolean;
}

export default function FormBuilder({ spec = [], possuiEdicao }: IFormBuilderProps) {

    const mapearTipoCampo = (type: string): string => {
        if (type.toLowerCase().startsWith('form_')) {
            return 'form_premontado';
        }

        const mapeamento: Record<string, string> = {
            'text': 'text',
            'email': 'email',
            'number': 'number',
            'select': 'select',
            'checkbox': 'checkbox',
            'radio': 'radio',
            'date': 'date',
            'file': 'file',
            'phone': 'phone',
            'switch': 'switch'
        };
        return mapeamento[type.toLowerCase()] || 'text';
    };

    const renderCampoPreview = useCallback((campo: ISpecFormulario) => {
        const tipoMapeado = mapearTipoCampo(campo.type);

        switch (tipoMapeado) {
            case 'form_premontado':
                return (
                    <Box
                        sx={{
                            p: 3,
                            borderRadius: 2,
                            textAlign: 'center',
                            backgroundColor: '#e3f2fd',
                            border: '2px dashed #1976d2',
                        }}
                    >
                        <Typography variant="h6" color="primary" sx={{ mb: 1 }}>
                            📋 Formulário Pré-montado
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                            {campo.label}
                        </Typography>
                    </Box>
                );

            case 'text':
            case 'email':
            case 'phone':
            case 'number':
                return (
                    <TextField
                        label={campo.label}
                        required={campo.required}
                        type={tipoMapeado === 'number' ? 'number' : 'text'}
                        fullWidth
                        variant="outlined"
                        placeholder={campo.mask || `Digite ${campo.label.toLowerCase()}`}
                        slotProps={{ htmlInput: { pattern: campo.mask && tipoMapeado === 'phone' ? "[0-9]*" : undefined } }}
                    />
                );

            case 'select':
                return (
                    <FormControl fullWidth required={campo.required}>
                        <InputLabel>{campo.label}</InputLabel>
                        <Select label={campo.label}>
                            {campo.options && campo.options.length > 0 ? (
                                campo.options.map((option: string, index: number) => (
                                    <MenuItem key={index} value={option}>
                                        {option}
                                    </MenuItem>
                                ))
                            ) : (
                                <MenuItem value="">Nenhuma opção disponível</MenuItem>
                            )}
                        </Select>
                    </FormControl>
                );

            case 'radio':
                return (
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {campo.label} {campo.required && '*'}
                        </Typography>
                        <RadioGroup>
                            {campo.options && campo.options.length > 0 ? (
                                campo.options.map((option: string, index: number) => (
                                    <FormControlLabel
                                        key={index}
                                        value={option}
                                        control={<Radio />}
                                        label={option}
                                    />
                                ))
                            ) : (
                                <Typography variant="body2" color="text.disabled">
                                    Nenhuma opção configurada
                                </Typography>
                            )}
                        </RadioGroup>
                    </Box>
                );

            case 'checkbox':
                return (
                    <FormControlLabel
                        control={<Checkbox />}
                        label={campo.label}
                    />
                );

            case 'date':
                return (
                    <TextField
                        label={campo.label}
                        type="date"
                        required={campo.required}
                        fullWidth
                        variant="outlined"
                        slotProps={{ inputLabel: { shrink: true } }}
                    />
                );

            case 'file':
                return (
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {campo.label} {campo.required && '*'}
                        </Typography>
                        <TextField
                            type="file"
                            fullWidth
                            variant="outlined"
                            slotProps={{ htmlInput: { accept: campo.mask || "*/*" } }}
                        />
                    </Box>
                );

            default:
                return (
                    <TextField
                        label={campo.label}
                        fullWidth
                        variant="outlined"
                    />
                );
        }
    }, []);

    return (
        <Paper sx={{ p: 3, height: '100%', overflow: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight="bold">
                    Área de Construção do Formulário
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip label={`${spec.length} campo(s)`} size="small" />
                    <Button
                        size="small"
                        startIcon={<AddIcon />}
                        variant="outlined"
                        disabled={!possuiEdicao}
                    >
                        Adicionar Campo
                    </Button>
                </Box>
            </Box>

            <Box
                sx={{
                    border: `2px dashed ${possuiEdicao ? '#e0e0e0' : '#ccc'}`,
                    borderRadius: 2,
                    p: 3,
                    minHeight: 250,
                    backgroundColor: possuiEdicao ? '#fafafa' : '#f8f8f8',
                    position: 'relative',
                }}
            >
                {spec.length === 0 ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: 300,
                            color: 'text.secondary'
                        }}
                    >
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            Nenhum campo configurado
                        </Typography>
                        <Typography variant="body2">
                            Adicione campos ao formulário ou arraste componentes da sidebar
                        </Typography>
                    </Box>
                ) : (
                    <Grid container spacing={3}>
                        {spec.map((campo, index) => {
                            const tipoMapeado = mapearTipoCampo(campo.type);
                            const isFormPremontado = tipoMapeado === 'form_premontado';

                            return (
                                <Grid size={{ xs: 12 }} key={index}>
                                    <Card
                                        sx={{
                                            border: `1px solid #e0e0e0`,
                                            backgroundColor: 'white',
                                            '&:hover': possuiEdicao ? {
                                                borderColor: '#1976d2',
                                                boxShadow: 2
                                            } : {}
                                        }}
                                    >
                                        <CardContent sx={{ pb: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                <DragIcon
                                                    color={possuiEdicao ? "action" : "disabled"}
                                                    sx={{
                                                        mr: 1,
                                                        cursor: possuiEdicao ? 'grab' : 'not-allowed'
                                                    }}
                                                />
                                                <Chip
                                                    label={isFormPremontado ? 'FORM PREMONTADO' : campo.type.toUpperCase()}
                                                    size="small"
                                                    color={'default'}
                                                    sx={{ mr: 2 }}
                                                />
                                                <Typography variant="body2" color="text.secondary">
                                                    {campo.name}
                                                </Typography>
                                                {campo.options && campo.options.length > 0 && (
                                                    <Chip
                                                        label={`${campo.options.length} opções`}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ ml: 1 }}
                                                    />
                                                )}
                                                <Box sx={{ ml: 'auto' }}>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        disabled={!possuiEdicao}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </Box>

                                            {renderCampoPreview(campo)}
                                        </CardContent>

                                        <CardActions sx={{ pt: 0, px: 2, pb: 2 }}>
                                            <Button
                                                size="small"
                                                disabled={!possuiEdicao}
                                            >
                                                Editar
                                            </Button>
                                            <Button
                                                size="small"
                                                disabled={!possuiEdicao}
                                            >
                                                Duplicar
                                            </Button>
                                            {campo.required && (
                                                <Chip label="Obrigatório" size="small" color="error" />
                                            )}
                                        </CardActions>
                                    </Card>
                                </Grid>
                            );
                        })}

                        {possuiEdicao && (
                            <Grid size={{ xs: 12 }}>
                                <Box
                                    sx={{
                                        border: '2px dashed #ccc',
                                        borderRadius: 2,
                                        p: 3,
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            borderColor: '#1976d2',
                                            backgroundColor: '#f5f5f5'
                                        }
                                    }}
                                >
                                    <AddIcon color="action" sx={{ fontSize: 40, mb: 1 }} />
                                    <Typography variant="body2" color="text.secondary">
                                        Clique aqui ou arraste um componente para adicionar um novo campo
                                    </Typography>
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                )}
            </Box>

            {spec.length > 0 && (
                <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                        Pré-visualização
                    </Typography>
                    <Paper
                        variant="outlined"
                        sx={{ p: 3, backgroundColor: '#f9f9f9' }}
                    >
                        <Grid container spacing={2}>
                            {spec.map((campo) => (
                                <Grid size={{ xs: 12, md: 6 }} key={campo.name}>
                                    {renderCampoPreview(campo)}
                                </Grid>
                            ))}
                        </Grid>
                    </Paper>
                </Box>
            )}
        </Paper>
    )
}